/**
 * Shared context helpers for the "Create New Class from Assign Class" round-trip.
 *
 * The Assign Class page opens Create Class carrying an explicit, validated
 * context (source + studentId + returnTo + prefill hints). Only when that
 * context is present does the form prefill from the student and return to the
 * same student's Assign Class page after a save. Normal class creation (no
 * source) is unaffected.
 */

export const ASSIGNMENT_SOURCE = "student-assignment";

const studentAssignRoute = (studentId: number | string) =>
  `/assign/classes/create/student/${studentId}`;

/** Only a numeric student Assign Class route is an allowed return target. */
const RETURN_TO_PATTERN = /^\/assign\/classes\/create\/student\/\d+$/;

export interface CreateClassContextInput {
  studentId: number | string;
  gradeId?: number | string | null;
  programType?: string | null;
  studyMode?: string | null;
  locationId?: number | string | null;
  /** Pre-fill the subject only when EXACTLY one subject chip is selected. */
  subjectSgId?: number | string | null;
}

/** Build the Create Class querystring for the assignment round-trip. */
export const buildCreateClassSearch = (input: CreateClassContextInput): string => {
  const p = new URLSearchParams();
  p.set("source", ASSIGNMENT_SOURCE);
  p.set("studentId", String(input.studentId));
  p.set("returnTo", studentAssignRoute(input.studentId));
  if (input.gradeId) p.set("grade_id", String(input.gradeId));
  if (input.programType) p.set("program_type", String(input.programType));
  if (input.studyMode) p.set("class_mode", String(input.studyMode));
  if (input.locationId) p.set("location_id", String(input.locationId));
  if (input.subjectSgId !== null && input.subjectSgId !== undefined && input.subjectSgId !== "") {
    p.set("subject_sg_id", String(input.subjectSgId));
  }
  return p.toString();
};

/** True when the current Create Class view was launched from Assign Class. */
export const isAssignmentContext = (
  source: string | null,
  studentId: string | null
): boolean => source === ASSIGNMENT_SOURCE && !!studentId;

/**
 * Resolve a SAFE return target. Returns "" for non-assignment sources (caller
 * keeps its normal destination). For the assignment source, a valid in-app
 * student route is returned as-is; anything else (external URL, tampered path)
 * falls back to the canonical route for the studentId — it can never redirect
 * outside the application.
 */
export const resolveSafeReturnTo = (
  source: string | null,
  studentId: string | null,
  returnTo: string | null | undefined
): string => {
  if (!isAssignmentContext(source, studentId)) return "";
  const rt = returnTo || "";
  return RETURN_TO_PATTERN.test(rt) ? rt : studentAssignRoute(studentId as string);
};

/* ──────────────────────────────────────────────────────────────────────────
   Generic Create-Class return context.

   ANY page that opens Create Class can pass `return_url` + `source` so that,
   after a successful create, the user is sent back to exactly the page they
   came from — instead of a single hardcoded destination. The return target is
   validated against a same-origin allowlist so a tampered/absolute URL can
   never redirect out of the app; anything invalid falls back to All Classes
   (`/classes/list`), never a student/staff profile.
   ────────────────────────────────────────────────────────────────────────── */

/** The page Create Class returns to when nothing valid is supplied. */
export const DEFAULT_CREATE_RETURN = "/classes/list";

// Same-origin in-app routes that are legitimate post-create destinations.
const RETURN_URL_ALLOWLIST = [
  /^\/classes\/list$/,
  /^\/class-schedule\/list$/,
  /^\/class-schedules$/,
  /^\/class-schedules\/\d+$/,
  /^\/check-staff\/classes\/list(\/\d+)?$/,
  /^\/team-history\/\d+$/,
  /^\/student-history\/\d+$/,
  /^\/staff\/[\w-]+(\/\d+)?$/,
  /^\/student\/[\w-]+(\/\d+)?$/,
  /^\/assign\/classes\/create\/student\/\d+$/,
];

/**
 * Validate a `return_url` and resolve where Create Class should navigate after
 * a successful save. Rejects absolute/protocol-relative/off-allowlist values.
 */
export const resolveSafeReturnUrl = (
  returnUrl: string | null | undefined,
  fallback: string = DEFAULT_CREATE_RETURN
): string => {
  if (!returnUrl) return fallback;
  // Only same-origin relative paths — never "//host", "https://…", etc.
  if (!returnUrl.startsWith("/") || returnUrl.startsWith("//")) return fallback;
  if (returnUrl.includes("://")) return fallback;
  const path = returnUrl.split("?")[0].split("#")[0];
  return RETURN_URL_ALLOWLIST.some((re) => re.test(path)) ? returnUrl : fallback;
};

/** Build the Create Class querystring carrying the return context. */
export const buildCreateReturnSearch = (
  returnUrl: string,
  source: string,
  extra?: Record<string, string | number | null | undefined>
): string => {
  const p = new URLSearchParams();
  p.set("source", source);
  p.set("return_url", returnUrl);
  if (extra) {
    Object.entries(extra).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") p.set(k, String(v));
    });
  }
  return p.toString();
};
